import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

export default function Header() {
  const [menuItems, setMenuItems] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const location = useLocation();

  // Fallback menu in case CMS fetch fails
  const fallbackMenu = [
    { label: { da: "Forside" }, href: "/", order: 1 },
    { label: { da: "Vi tilbyder" }, href: "#", order: 2 },
    { label: { da: "Hvem er vi" }, href: "/om-os", order: 3 },
    { label: { da: "Projekter" }, href: "/referencer", order: 4 },
    { label: { da: "Galleri" }, href: "/galleri", order: 5 },
    { label: { da: "Kontakt" }, href: "/kontakt", order: 6 }
  ];

  const fallbackSubmenu = [
    { label: { da: "Sommerhusrenovering" }, href: "/ydelser/sommerhusrenovering", parent: "Vi tilbyder" },
    { label: { da: "Tilbygning" }, href: "/ydelser/tilbygning", parent: "Vi tilbyder" },
    { label: { da: "Ombygning" }, href: "/ydelser/ombygning", parent: "Vi tilbyder" },
    { label: { da: "Tømrerarbejde" }, href: "/ydelser/toemrer", parent: "Vi tilbyder" },
    { label: { da: "Maler på Djursland" }, href: "/ydelser/maler", parent: "Vi tilbyder" },
    { label: { da: "Udskiftning af vinduer og døre" }, href: "/ydelser/udskiftning-af-vinduer", parent: "Vi tilbyder" },
    { label: { da: "Nyt tag" }, href: "/ydelser/asbestarbejde-og-tagrenovering", parent: "Vi tilbyder" }
  ];

  useEffect(() => {
    const fetchMenu = async () => {
      try {
        const response = await fetch('https://api.dev.auxo.dk/public/content/navigation');
        if (response.ok) {
          const data = await response.json();
          if (Array.isArray(data) && data.length > 0) {
            setMenuItems(data);
            return;
          }
        }
        // If empty or failed, use fallback
        setMenuItems([...fallbackMenu, ...fallbackSubmenu]);
      } catch (err) {
        setMenuItems([...fallbackMenu, ...fallbackSubmenu]);
      }
    };

    fetchMenu();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1200);
      if (window.innerWidth >= 1200) {
        setIsOpen(false);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close menu on route change
  useEffect(() => {
    setIsOpen(false);
    setIsDropdownOpen(false);
  }, [location]);

  // Organize menu hierarchy
  const mainItems = menuItems
    .filter(item => !item.parent)
    .sort((a, b) => a.order - b.order);

  const getChildren = (parentLabel) => {
    return menuItems
      .filter(item => item.parent === parentLabel)
      .sort((a, b) => a.order - b.order);
  };

  const isActive = (href) => {
    if (href === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(href);
  };

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-slate-100 transition-all">
      <div className="max-w-7xl mx-auto px-6 h-20 sm:h-28 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3">
          <img
            src="https://assets.auxodev.dk/assets/d1f69564-4a06-4909-b6f1-fa1f6ad6911e/logo.png"
            alt="Tømrerfirmaet RNS - Din lokale tømrer og maler på Djursland"
            className="h-14 sm:h-24 w-auto object-contain"
          />
        </Link>

        {/* Desktop Navigation */}
        {!isMobile ? (
          <nav className="flex items-center gap-8">
            {mainItems.map((item) => {
              const children = getChildren(item.label.da);
              const hasChildren = children.length > 0;

              if (hasChildren) {
                return (
                  <div
                    key={item.id || item.slug}
                    className="relative"
                    onMouseEnter={() => setIsDropdownOpen(true)}
                    onMouseLeave={() => setIsDropdownOpen(false)}
                  >
                    <button
                      className={`flex items-center gap-1.5 font-bold text-xs uppercase tracking-wider transition-colors py-2 cursor-pointer ${
                        children.some(child => isActive(child.href))
                          ? 'text-black font-extrabold'
                          : 'text-slate-700 hover:text-brand-primary'
                      }`}
                    >
                      {item.label.da}
                      <i className={`fa-solid fa-chevron-down text-[10px] transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {/* Dropdown Menu */}
                    {isDropdownOpen && (
                      <div className="absolute top-full left-0 w-64 bg-white border border-slate-100 rounded-xl shadow-lg py-2 mt-1 animate-in fade-in slide-in-from-top-2 duration-200">
                        {children.map((child) => (
                          <Link
                            key={child.id || child.slug}
                            to={child.href}
                            className={`block px-5 py-2.5 text-xs uppercase tracking-wider transition-colors font-bold ${
                              isActive(child.href)
                                ? 'bg-slate-50 text-black font-extrabold'
                                : 'text-slate-700 hover:bg-slate-50 hover:text-brand-primary'
                            }`}
                          >
                            {child.label.da}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <Link
                  key={item.id || item.slug}
                  to={item.href}
                  className={`font-bold text-xs uppercase tracking-wider transition-colors py-2 ${
                    isActive(item.href)
                      ? 'text-black font-extrabold border-b-2 border-black'
                      : 'text-slate-700 hover:text-brand-primary'
                  }`}
                >
                  {item.label.da}
                </Link>
              );
            })}

            {/* CTA Button */}
            <Link
              to="/kontakt"
              className="bg-brand-primary hover:bg-brand-primary/90 text-white font-semibold text-sm py-2.5 px-5 rounded-none transition-all shadow-sm"
            >
              Få et tilbud
            </Link>
          </nav>
        ) : (
          /* Mobile Hamburger Button */
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="p-2 text-slate-600 hover:text-brand-primary focus:outline-none cursor-pointer"
            aria-label="Toggle menu"
          >
            <i className={`fa-solid ${isOpen ? 'fa-xmark' : 'fa-bars'} text-2xl`} />
          </button>
        )}
      </div>

      {/* Mobile Menu Panel */}
      {isMobile && isOpen && (
        <div className="border-t border-slate-100 bg-white py-4 px-6 space-y-3 shadow-inner animate-in slide-in-from-top duration-200">
          {mainItems.map((item) => {
            const children = getChildren(item.label.da);
            const hasChildren = children.length > 0;

            if (hasChildren) {
              return (
                <div key={item.id || item.slug} className="space-y-1">
                  <button
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className="w-full flex items-center justify-between font-semibold text-xs uppercase tracking-wider text-slate-600 py-2 text-left"
                  >
                    <span>{item.label.da}</span>
                    <i className={`fa-solid fa-chevron-down text-xs transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {isDropdownOpen && (
                    <div className="pl-4 border-l-2 border-slate-100 space-y-1 py-1">
                      {children.map((child) => (
                        <Link
                          key={child.id || child.slug}
                          to={child.href}
                          className={`block py-2 text-xs uppercase tracking-wider transition-colors ${
                            isActive(child.href)
                              ? 'text-black font-bold'
                              : 'text-slate-500 hover:text-brand-primary'
                          }`}
                        >
                          {child.label.da}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            }

            return (
              <Link
                key={item.id || item.slug}
                to={item.href}
                className={`block font-semibold text-xs uppercase tracking-wider py-2 transition-colors ${
                  isActive(item.href)
                    ? 'text-black font-bold'
                    : 'text-slate-600 hover:text-brand-primary'
                }`}
              >
                {item.label.da}
              </Link>
            );
          })}

          <div className="pt-4 border-t border-slate-100">
            <Link
              to="/kontakt"
              className="w-full bg-brand-primary hover:bg-brand-primary/90 text-white font-semibold py-3 px-4 rounded-none transition-all shadow-sm flex items-center justify-center gap-2"
            >
              <i className="fa-solid fa-phone" />
              Få et uforpligtende tilbud
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
